import { AnalyticsSettings } from './analyticsService.d';
import { analyticsUtils } from './analyticsUtils';
import { reportWebVitals } from './reportWebVitals';
import { vendors } from './vendors';

const { getTitleFromRoute, serviceOptions } = analyticsUtils;

let previousPageTitle: string;

const amplitude = vendors.amplitude(serviceOptions);
const facebook = vendors.facebook(serviceOptions);
const smartlook = vendors.smartlook(serviceOptions);

function setupAmplitude() {
  amplitude.addVendor();

  try {
    const amplitudeId = process.env.REACT_APP_AMPLITUDE_API_KEY;
    amplitude.initialise(amplitudeId);
  } catch (error) {
    console.log(error);
  }
}

function setupFacebook(facebookIds) {
  facebook.addVendor();

  const fbPixelIds = facebookIds;
  if (fbPixelIds.length > 0) {
    fbPixelIds.forEach((pixelId) => facebook.initialise(pixelId));
  }
}

function setupSmartlook() {
  smartlook.addVendor();

  try {
    const smartlookId = process.env.REACT_APP_SMARTLOOK_API_KEY;
    smartlook.initialise(smartlookId);
  } catch (error) {
    console.log(error);
  }
}

function initialise(
  analyticsSettings: AnalyticsSettings,
  cookieSettings?: undefined | string[],
): boolean {
  const isProduction = process.env.REACT_APP_ENVIRONMENT === 'production';
  if (!isProduction) {
    return false;
  }

  try {
    const isCookieConsentRequired = cookieSettings !== undefined;
    const isAdvertisingEnabled =
      cookieSettings && cookieSettings?.includes('advertising');
    const isPerformanceEnabled =
      cookieSettings && cookieSettings?.includes('performance');

    if (!isCookieConsentRequired) {
      reportWebVitals(analyticsSettings.uiVersion);
      setupAmplitude();
      setupFacebook(analyticsSettings.facebookIds);
      setupSmartlook();
      return true;
    }

    if (isPerformanceEnabled) {
      reportWebVitals(analyticsSettings.uiVersion);
      setupAmplitude();
      setupSmartlook();
    } else {
      amplitude.revokeConsent();
      smartlook.revokeConsent();
    }

    if (isAdvertisingEnabled) {
      setupFacebook(analyticsSettings.facebookIds);
    } else {
      facebook.revokeConsent();
    }

    return true;
  } catch (error) {
    serviceOptions.logger(`Error: initialise: ${error}`);
  }

  return false;
}

function pageView(): void {
  const isProduction = process.env.REACT_APP_ENVIRONMENT === 'production';
  if (!isProduction) {
    return;
  }

  const pageTitle = getTitleFromRoute(window.location.hash);
  const isDuplicatePageView = previousPageTitle === pageTitle;

  if (isDuplicatePageView || !pageTitle) {
    return;
  }

  try {
    facebook.pageView();
    smartlook.pageView(pageTitle);

    previousPageTitle = pageTitle;
  } catch (error) {
    serviceOptions.logger(`Error: pageView: ${error}`);
  }
}

function track(data: Record<string, unknown>): void {
  const isProduction = process.env.REACT_APP_ENVIRONMENT === 'production';

  if (isProduction) {
    try {
      facebook.trackEvent(data);
      amplitude.trackEvent(data);
    } catch (error) {
      serviceOptions.logger(`Error: track: ${error}`);
    }
  }
}

export const analyticsService = {
  initialise,
  pageView,
  track,
};
